import type { SituationCoverageEntry } from '@/types/spray';

/** One step up a situation's coverage: a group, and how many spellings it has. */
export interface CoverageStep {
  name: string;
  /** Other spellings folded into this group. */
  spellings: number;
}

/**
 * Fold a situation's coverage into its chain of groups, nearest first:
 * ALMOND → TREE NUT PLANTATION (+2 spellings) → TREEFRUIT.
 *
 * The first step is named after the situation itself, even when it is a
 * spelling rather than its group's canonical, because that is the name the
 * person picked. Every broader step is named after its canonical row.
 */
export function coverageSteps(coverage: SituationCoverageEntry[] | null | undefined): CoverageStep[] {
  if (!coverage?.length) return [];
  const byDepth = new Map<number, SituationCoverageEntry[]>();
  for (const c of coverage) {
    if (!byDepth.has(c.depth)) byDepth.set(c.depth, []);
    byDepth.get(c.depth)!.push(c);
  }
  return [...byDepth.keys()].sort((a, b) => a - b).map(depth => {
    const members = byDepth.get(depth)!;
    const named = depth === 0
      ? members.find(m => m.relation === 'self') ?? members[0]
      : members.find(m => m.canonical) ?? members[0];
    return { name: named.name, spellings: members.length - 1 };
  });
}

/** "ALMOND → TREE NUT PLANTATION (+2 spellings) → TREEFRUIT" */
export function describeCoverage(coverage: SituationCoverageEntry[] | null | undefined): string {
  return coverageSteps(coverage)
    .map(s => s.spellings > 0 ? `${s.name} (+${s.spellings} spelling${s.spellings === 1 ? '' : 's'})` : s.name)
    .join(' → ');
}
